import { Icon } from '../../components/ui/Icon.jsx';
import { ExamCard } from '../../components/ExamCard.jsx';
import { fmtDate } from '../../lib/format.js';
import { useSola, solaNastavitve } from '../../sola.jsx';
import { t } from '../../i18n/index.js';

/*
  Prijava na izpit.

  Vsebina je povzeta po perut.net/blog/solanje-5/prijava-na-izpit-5 in
  povezana z dejanskim stanjem tečajnika — da ne bere splošnih navodil,
  ampak vidi, kje konkretno je.

  Zdravniški pregled je tu izpostavljen, ker ga je treba opraviti NA ZAČETKU
  tečaja, ne pred izpitom. Tečajniki ga pogosto razumejo kot zadnjo
  formalnost, pregled za B kategorijo pa se naroča vnaprej.
*/

export function Izpit({ check, onGoDocs }) {
  const { sola } = useSola();
  const { agencija } = solaNastavitve(sola);
  const pogoji = check.pogoji;
  const med = check.med;
  const leti = check.items.find((i) => i.key === 'leti');
  const vzletisca = check.items.find((i) => i.key === 'vzletisca');

  return (
    <div className="stack stack--loose">
      {/* Zdravniški je prvi, ker sodi na začetek tečaja, ne pred izpit. */}
      <MedicalCard med={med} />

      <ExamCard check={check} compact />

      <section className="card">
        <div className="card__head">
          <span className="card__title">{t('Kaj potrebuješ za prijavo')}</span>
        </div>

        <div className="card__body stack stack--loose">
          <Step
            n={1}
            title={t('Izpolnjena knjižica letenja')}
            ok={leti?.ok && vzletisca?.ok}
            status={`${check.letov}/${pogoji.min_letov} ${t('letov')} · ${check.vzletisc}/${pogoji.min_vzletisc} ${t('vzletišč')}`}
          >
            {t('Vsaj {a} letov z nadaljevalnega tečaja, na vsaj {b} različnih vzletiščih, z višinsko razliko vsaj {c} m.', {
              a: pogoji.min_letov,
              b: pogoji.min_vzletisc,
              c: pogoji.visinska_razlika,
            })}
            <br />
            <span className="t-dimmer">
              {t('Višinske razlike aplikacija ne beleži — to preveri inštruktor ob pregledu knjižice.')}
            </span>
          </Step>

          <Step
            n={2}
            title={t('Zdravniški pregled za B kategorijo')}
            ok={med?.ok}
            status={med?.examined ? t('velja do {d}', { d: fmtDate(med.expires) }) : t('ni vpisan')}
          >
            <strong>{t('Opravi ga na začetku tečaja, ne pred izpitom.')}</strong>{' '}
            {t('Ne sme biti starejši od {n} mesecev.', { n: pogoji.zdravniski_meseci })}{' '}
            <strong>{t('Noben drug zdravniški pregled ni enakovreden')}</strong>{' '}
            {t('potrdilu za B kategorijo (avto).')} {t('Fotografijo naloži med')}{' '}
            {onGoDocs ? (
              <button type="button" className="t-brand t-bold" style={{ textDecoration: 'underline', padding: 0 }} onClick={onGoDocs}>
                {t('Dokumente')}
              </button>
            ) : (
              t('Dokumente')
            )}
            {t(', datum pregleda vpiše inštruktor.')}
          </Step>

          <Step n={3} title={t('Prijavnica')} status={agencija.prijavnica_ime}>
            {t('Uradni obrazec agencije. Izpolni svoje podatke in obkljukaj')}{' '}
            <strong>{t('Jadralno padalo (PG)')}</strong>{' '}
            {t('ter del izpita, na katerega se prijavljaš. Spodnji del izpolni in podpiše inštruktor — prijavnico mu prinesi, preden jo pošlješ.')}
            <br />
            {agencija.prijavnica_url && (
              <a
                className="btn btn--secondary btn--sm"
                style={{ marginTop: 'var(--s-2)' }}
                href={agencija.prijavnica_url}
                download
              >
                <Icon name="download" size={15} /> {t('Prenesi prijavnico')}
              </a>
            )}
          </Step>
        </div>
      </section>

      <section className="card">
        <div className="card__head">
          <span className="card__title">{t('Kam pošlješ')}</span>
        </div>
        <div className="card__body stack stack--tight">
          <p className="t-md">
            {t('Potrjeno prijavnico in')} <strong>{t('kopijo zdravniškega pregleda')}</strong> {t('pošlji')}{' '}
            <strong>{t('priporočeno')}</strong> {t('na:')}
          </p>
          <address className="card card--pad t-md" style={{ whiteSpace: 'pre-line', fontStyle: 'normal' }}>
            {agencija.naslov}
          </address>
          <p className="t-sm t-dim">
            {t('Prijavi se pravočasno — roki so vnaprej razpisani, zdravniški pregled pa se naroča vnaprej.')}
          </p>
          <div className="row row--wrap" style={{ gap: 'var(--s-2)' }}>
            {agencija.roki_url && (
              <a
                className="btn btn--secondary btn--sm"
                href={agencija.roki_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="calendar" size={15} /> {t('Izpitni roki (PDF)')}
              </a>
            )}
            {agencija.url && (
              <a
                className="btn btn--ghost btn--sm"
                href={agencija.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {agencija.naziv}
              </a>
            )}
          </div>
        </div>
      </section>

      <section className="card">
        <div className="card__head">
          <span className="card__title">{t('Vprašanja')}</span>
        </div>
        <div className="card__body stack stack--tight">
          {sola?.email && (
            <a className="row t-md" href={`mailto:${sola.email}`}>
              <Icon name="mail" size={17} className="t-dimmer" />
              {sola.email}
            </a>
          )}
          {sola?.telefon && (
            <a className="row t-md" href={`tel:${sola.telefon.replace(/\s/g, '')}`}>
              <Icon name="phone" size={17} className="t-dimmer" />
              {sola.telefon}
            </a>
          )}
          {sola?.naslov && (
            <p className="row t-md t-dim">
              <Icon name="idCard" size={17} className="t-dimmer" />
              {sola.naslov}
            </p>
          )}
          {agencija.blog_url && (
            <a
              className="btn btn--ghost btn--sm"
              style={{ alignSelf: 'flex-start', marginTop: 'var(--s-2)' }}
              href={agencija.blog_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('Celotna navodila šole')}
            </a>
          )}
        </div>
      </section>
    </div>
  );
}

/* ── Zdravniški — stalni opomnik ────────────────────────────
   Dokler ni urejen, mora biti opazen: brez njega prijava ne gre skozi,
   pregled pa se naroča vnaprej.                                      */

function MedicalCard({ med }) {
  if (med?.ok && !med.expiringSoon) {
    return (
      <section className="card card--ok card--pad row row--top">
        <Icon name="check" size={24} className="t-ok" />
        <div className="spacer">
          <h2 className="t-lg t-ok">{t('Zdravniški pregled je urejen')}</h2>
          <p className="t-sm t-dim">{t('Velja do')} {fmtDate(med.expires)}.</p>
        </div>
      </section>
    );
  }

  const tone = med?.valid ? 'wait' : 'bad';
  const naslov = !med?.uploaded
    ? t('Opravi zdravniški pregled za B kategorijo')
    : !med?.examined
      ? t('Manjka datum zdravniškega pregleda')
      : !med?.valid
        ? t('Zdravniški pregled je potekel')
        : t('Zdravniški pregled poteče čez {n} dni', { n: med.daysLeft });

  return (
    <section
      className="card card--pad row row--top"
      style={{
        background: `var(--${tone}-wash)`,
        borderColor: `var(--${tone}-border)`,
      }}
      role="status"
    >
      <Icon name="alert" size={24} className={tone === 'bad' ? 't-bad' : 't-wait'} />
      <div className="spacer stack stack--tight">
        <h2 className={`t-lg ${tone === 'bad' ? 't-bad' : 't-wait'}`}>{naslov}</h2>

        <p className="t-md">
          {t('Pregled za')} <strong>{t('B kategorijo (avto)')}</strong> {t('sodi na')}{' '}
          <strong>{t('začetek tečaja')}</strong>
          {t(', ne pred izpit. Brez njega ne smeš leteti, prijave na izpit pa ni mogoče oddati. Noben drug zdravniški pregled ni enakovreden.')}
        </p>

        {med?.examined && !med.valid && (
          <p className="t-sm t-dim">{t('Zadnji pregled je potekel {d}.', { d: fmtDate(med.expires) })}</p>
        )}

        <p className="t-sm t-dim">
          {t('Naroči se čim prej — termina se pogosto čaka. Ko pregled opraviš, fotografijo naloži med Dokumente.')}
        </p>
      </div>
    </section>
  );
}

function Step({ n, title, children, ok, status }) {
  return (
    <div className="row row--top" style={{ gap: 'var(--s-3)' }}>
      <span
        aria-hidden="true"
        style={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          fontWeight: 800,
          fontSize: 12,
          background: ok ? 'var(--ok-fill)' : 'var(--surface-3)',
          color: ok ? '#fff' : 'var(--text-2)',
        }}
      >
        {ok ? <Icon name="check" size={14} strokeWidth={2.6} /> : n}
      </span>

      <div className="spacer">
        <div className="row row--wrap" style={{ gap: 'var(--s-2)' }}>
          <span className="t-md t-bold">{title}</span>
          {status && (
            <span className={`badge ${ok ? 'badge--ok' : 'badge--mute'} num`}>{status}</span>
          )}
        </div>
        <p className="t-sm t-dim" style={{ marginTop: 2 }}>
          {children}
        </p>
      </div>
    </div>
  );
}
